import React from 'react';
import { cn } from '@/utils/cn';

export interface TopBarIconButtonProps {
  icon: React.ReactNode;
  /** Already-translated accessible label (e.g. "Inbox"). */
  label: string;
  /** Already-translated suffix used when count > 0, defaults to "unread". */
  countLabel?: string;
  count?: number;
  onClick: () => void;
  active?: boolean;
  className?: string;
}

export const TopBarIconButton: React.FC<TopBarIconButtonProps> = ({
  icon,
  label,
  countLabel = 'unread',
  count,
  onClick,
  active,
  className,
}) => {
  const hasCount = typeof count === 'number' && count > 0;
  const display = hasCount && count > 99 ? '99+' : String(count);
  const ariaLabel = hasCount ? `${label}, ${count} ${countLabel}` : label;

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'relative inline-flex items-center justify-center w-10 h-10 rounded-[8px] text-[var(--text-on-forest)] transition-colors',
        'hover:bg-[rgba(251,246,236,0.12)] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--border-focus)]',
        active && 'bg-[rgba(251,246,236,0.16)]',
        className
      )}
    >
      <span className="inline-flex items-center justify-center w-9 h-9">
        {icon}
      </span>
      {hasCount && (
        <span
          data-testid="top-bar-badge"
          aria-hidden="true"
          className="absolute -top-[3px] -right-[5px] min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-[var(--color-gold-500)] text-[var(--text-content-title)] text-[12px] font-bold tabular-nums ring-2 ring-[var(--color-forest)]"
        >
          {display}
        </span>
      )}
    </button>
  );
};

export default TopBarIconButton;
